import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Tooltip,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Avatar,
  FormControlLabel,
  IconButton,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  AlertTitle,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  CloudUpload as CloudUploadIcon,
  Save as SaveIcon,
  AccountCircle as AccountCircleIcon,
  ContactPhone as ContactPhoneIcon,
  School as SchoolIcon,
  LocalHospital as LocalHospitalIcon,
  Person as PersonIcon,
  Notes as NotesIcon,
  ChevronLeft as ChevronLeftIcon,
  ContactMail as ContactMailIcon,
  Home as HomeIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Work as WorkIcon,
  ContactEmergency as ContactEmergencyIcon,
  Badge as BadgeIcon,
  Cake as CakeIcon,
  Wc as WcIcon,
  CheckCircle as CheckCircleIcon,
  Search as SearchIcon,
  Info as InfoIcon,
  Error as ErrorIcon,
  MedicalServices as MedicalServicesIcon,
  Psychology as PsychologyIcon,
  HealthAndSafety as HealthAndSafetyIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useNotification } from '../../contexts/NotificationContext';
import pacienteService from '../../services/pacienteService';
import consultaService from '../../services/consultaService';
import especialidadService from '../../services/especialidadService';
import especialistaService from '../../services/especialistaService';
import PageHeader from '../../components/common/PageHeader';
import ImageUpload from '../../components/forms/ImageUpload';

const PacienteForm = ({ isEdit = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(isEdit);
  const [previewImage, setPreviewImage] = useState(null);
  const [apoderados, setApoderados] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  
  // Estados para especialidades y especialistas
  const [especialidades, setEspecialidades] = useState([]);
  const [especialistas, setEspecialistas] = useState([]);
  const [asignaciones, setAsignaciones] = useState([]);
  const [nuevaAsignacion, setNuevaAsignacion] = useState({
    especialidad_id: '',
    especialista_id: ''
  });

  const getEspecialistasPorEspecialidad = (especialidadId) => {
  if (!especialidadId || !especialistas.length) {
    return [];
  }
  
  const especialidadIdStr = String(especialidadId);
  
  // Filtrar especialistas que tengan esta especialidad
  return especialistas.filter(especialista => {
    if (!especialista.especialidades || !Array.isArray(especialista.especialidades)) {
      return false;
    }
    
    return especialista.especialidades.some(esp => 
      String(esp.id) === especialidadIdStr
    );
  });
};

  // Función para agregar una asignación
  const agregarAsignacion = () => {
    
    // Validar que se haya seleccionado especialidad y especialista
    if (!nuevaAsignacion.especialidad_id || !nuevaAsignacion.especialista_id) {
      showNotification('Debe seleccionar especialidad y especialista', 'warning');
      return;
    }
    
     const especialistaYaAsignado = asignaciones.some(
      a => String(a.especialista_id) === String(nuevaAsignacion.especialista_id)
    );
    
    if (especialistaYaAsignado) {
      showNotification('Este especialista ya ha sido asignado al paciente', 'warning');
      return;
    }
    
    // Verificar si ya existe la misma especialidad-especialista
    const asignacionExistente = asignaciones.some(
      a => String(a.especialista_id) === String(nuevaAsignacion.especialista_id)
    );
    
    if (asignacionExistente) {
      showNotification('Este especialista ya ha sido asignado', 'warning');
      return;
    }
    
    // Buscar el nombre de la especialidad y especialista para mostrar en UI
    const especialidad = especialidades.find(e => String(e.id) === String(nuevaAsignacion.especialidad_id));
    const especialista = especialistas.find(e => String(e.id) === String(nuevaAsignacion.especialista_id));
    
    const especialistasEnEstaEspecialidad = asignaciones.filter(
      a => String(a.especialidad_id) === String(nuevaAsignacion.especialidad_id)
    );
    
    if (especialistasEnEstaEspecialidad.length > 0) {
      showNotification(
        `Se agregó otro especialista a ${especialidad?.nombre}. Compartirán la misma Historia Clínica.`, 
        'info'
      );
    }
    
    // Agregar la nueva asignación con un ID temporal único
    const nuevasAsignaciones = [
      ...asignaciones, 
      { 
        temp_id: Date.now(), // ID temporal para manipulación en UI
        especialidad_id: nuevaAsignacion.especialidad_id,
        especialista_id: nuevaAsignacion.especialista_id,
        especialidad_nombre: especialidad?.nombre || 'Especialidad desconocida',
        especialista_nombre: especialista ? `${especialista.nombre || ''} ${especialista.apellidos || ''}`.trim() : 'Especialista desconocido',
        fecha_asignacion: new Date().toISOString().split('T')[0],
        motivo_asignacion: "Asignación desde formulario de paciente",
        estado: 'Activo',
        isNew: true, // Marcar como nueva asignación
        compartirHCE: especialistasEnEstaEspecialidad.length > 0 // NUEVO: Indicar si comparte HCE
      }
    ];
    
   
    setAsignaciones(nuevasAsignaciones);
    
    // Resetear el formulario
    setNuevaAsignacion({
      especialidad_id: '',
      especialista_id: ''
    });
    
    showNotification('Especialista asignado correctamente', 'success');
  };

  // Función para eliminar una asignación
  const eliminarAsignacion = (index) => {
    const nuevasAsignaciones = [...asignaciones];
    nuevasAsignaciones.splice(index, 1);
    setAsignaciones(nuevasAsignaciones);
    showNotification('Asignación eliminada', 'success');
  };

  // Obtener nombre de especialidad por ID
  const getNombreEspecialidad = (id) => {
    if (!id) return 'No asignada';
    const especialidad = especialidades.find(e => String(e.id) === String(id));
    return especialidad ? especialidad.nombre : `Especialidad #${id}`;
  };

  // Obtener nombre de especialista por ID
  const getNombreEspecialista = (id) => {
    if (!id) return 'No asignado';
    const especialista = especialistas.find(e => String(e.id) === String(id));
    if (!especialista) return `Especialista #${id}`;
    return especialista.nombreCompleto || `${especialista.nombre || ''} ${especialista.apellidos || ''}`.trim();
  };

  // Cargar especialidades y especialistas al inicio
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // 1. Cargar especialidades
        const respEspecialidades = await especialidadService.getEspecialidades();
        const especialidadesData = respEspecialidades.data || [];
         
        setEspecialidades(especialidadesData);
        
        // 2. Cargar especialistas
        const respEspecialistas = await especialistaService.getEspecialistas();
        const especialistasData = respEspecialistas.data || [];
        
        // Normalizar especialistas - MANTENER la estructura que viene del backend
        const especialistasNormalizados = especialistasData.map(esp => ({
          id: String(esp.id),
          nombre: esp.usuario?.nombre || '',
          apellidos: esp.usuario?.apellidos || '',
          nombreCompleto: `${esp.usuario?.nombre || ''} ${esp.usuario?.apellidos || ''}`.trim(),
          especialidades: esp.especialidades || [] // Mantener el array de objetos tal cual
        }));
        
         
        setEspecialistas(especialistasNormalizados);
        
        // 3. Si es edición, NO cargar el paciente aquí todavía
        if (!isEdit) {
          setLoading(false);
        }
        
      } catch (error) {
        console.error('Error al cargar datos:', error);
        showNotification('Error al cargar datos de configuración', 'error');
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  useEffect(() => {
    if (isEdit && id && especialidades.length > 0 && especialistas.length > 0) {
       
      fetchPatient();
    }
  }, [isEdit, id, especialidades.length, especialistas.length]);

  // Función para preparar datos antes de enviar
  const prepareDataForSubmission = (values) => {
    const dataToSend = { ...values };
    
    // Excluir campos que no deben enviarse directamente
    delete dataToSend.fotoFile;
    delete dataToSend.asignaciones; // Agregar esta línea
    delete dataToSend.asignaciones_especialistas; // Y esta
    
    return dataToSend;
  };

  // Función para cargar datos del paciente
  const fetchPatient = async () => {
    if (isEdit && id) {
      setLoading(true);
      try {
        // IMPORTANTE: Primero asegurarse de que especialidades y especialistas estén cargados
        if (especialidades.length === 0 || especialistas.length === 0) {
          
          setTimeout(() => fetchPatient(), 500); // Reintentar en 500ms
          return;
        }

        // Obtener datos del paciente
        const response = await pacienteService.getPaciente(id);
        const patient = response.data;
        
 
        
        // Mapear datos básicos del paciente
        const mappedPatient = {
          ...patient,
          fecha_nacimiento: patient.fecha_nacimiento ? new Date(patient.fecha_nacimiento) : null,
        };
        
        // Cargar apoderados
        if (patient.apoderados && Array.isArray(patient.apoderados)) {
          setApoderados(patient.apoderados);
        }
        
        // Establecer valores en el formulario
        formik.setValues(mappedPatient);
        
        // CARGAR ASIGNACIONES EXISTENTES
        try {
         
          const especialistasResponse = await pacienteService.getPacienteEspecialistas(id);
          
          if (especialistasResponse && especialistasResponse.data && Array.isArray(especialistasResponse.data)) {
        
            
            const asignacionesCargadas = [];
            
            for (const relacion of especialistasResponse.data) {
              // Solo procesar las relaciones activas
              if (!relacion.activo) continue;
              
              // Buscar el especialista en nuestra lista
              const especialistaEncontrado = especialistas.find(
                e => String(e.id) === String(relacion.especialista?.id || relacion.especialista_id)
              );
              
              if (!especialistaEncontrado) continue;
              
              // Obtener la primera especialidad del especialista
              let especialidadId = null;
              let especialidadNombre = 'Especialidad desconocida';
              
              if (especialistaEncontrado.especialidades && especialistaEncontrado.especialidades.length > 0) {
                especialidadId = String(especialistaEncontrado.especialidades[0].id);
                especialidadNombre = especialistaEncontrado.especialidades[0].nombre;
              }
              
              asignacionesCargadas.push({
                id: relacion.id,
                especialista_id: String(relacion.especialista?.id || relacion.especialista_id),
                especialidad_id: especialidadId,
                especialista_nombre: especialistaEncontrado.nombreCompleto || 
                  `${especialistaEncontrado.nombre || ''} ${especialistaEncontrado.apellidos || ''}`.trim(),
                especialidad_nombre: especialidadNombre,
                fecha_asignacion: relacion.fecha_asignacion || new Date().toISOString().split('T')[0],
                motivo_asignacion: relacion.motivo_asignacion || 'Asignación previa',
                estado: relacion.activo ? 'Activo' : 'Inactivo',
                isNew: false // No es una nueva asignación
              });
            }
            
        
            setAsignaciones(asignacionesCargadas);
          }
          
        } catch (error) {
          console.error('Error cargando especialistas del paciente:', error);
          // No fallar completamente, solo mostrar warning
          showNotification('Advertencia: No se pudieron cargar las asignaciones de especialistas', 'warning');
        }
        
      } catch (error) {
        console.error('Error cargando paciente:', error);
        showNotification('Error al cargar los datos del paciente', 'error');
      } finally {
        setLoading(false);
      }
    }
  };

  // Esquema de validación con Yup
  const validationSchema = Yup.object({
    nombres: Yup.string().required('El nombre es requerido'),
    apellidos: Yup.string().required('Los apellidos son requeridos'),
  });

  // Valores iniciales del formulario
  const initialValues = {
    nombres: '',
    apellidos: '',
    dni: '',
    fecha_nacimiento: null,
    sexo: '',
    edad: '',
    grado_academico: '',
    institucion_educativa: '',
    trabajo_actual: '',
    direccion: '',
    telefono: '',
    whatsapp: '',
    telefono_emergencia: '',
    contacto_emergencia: '',
    email: '',
    grupo_sanguineo: '',
    alergias: '',
    condiciones_preexistentes: '',
    medicacion_actual: '',
    estado: 'Activo',
    escolaridad: '',
    lateralidad: null,  
    notas_administrativas: '',
    foto: '',
    fotoFile: null,
  };

  const formik = useFormik({
  initialValues,
  validationSchema,
  onSubmit: async (values) => {
  try {
    // Preparar datos para envío
    const dataToSend = prepareDataForSubmission(values);
    
    let pacienteId = id;
    
    if (isEdit) {
      // Actualizar paciente existente
      await pacienteService.updatePaciente(id, dataToSend);
      
      // Guardar apoderados
      for (const apoderado of apoderados) {
        if (apoderado.isNew) {
          await pacienteService.createApoderado(id, apoderado);
        } else if (apoderado.isModified) {
          await pacienteService.updateApoderado(id, apoderado.id, apoderado);
        }
      }
      
      // MEJORADO: Gestión de asignaciones de especialistas
      try {
        
        // 1. Obtener asignaciones actuales del servidor
        const asignacionesActualesResp = await pacienteService.getPacienteEspecialistas(id);
        const asignacionesActuales = asignacionesActualesResp.data || [];
        
        // 2. Identificar asignaciones a eliminar
        const asignacionesAEliminar = asignacionesActuales.filter(actual => {
          if (!actual.activo) return false;
          
          const especialistaIdActual = String(actual.especialista?.id || actual.especialista_id);
          const existeEnNuevas = asignaciones.some(nueva => 
            String(nueva.especialista_id) === especialistaIdActual
          );
          
          return !existeEnNuevas;
        });
        
        // 3. Eliminar asignaciones
        for (const eliminar of asignacionesAEliminar) {
          const especialistaId = eliminar.especialista?.id || eliminar.especialista_id;
          if (especialistaId) {
            await pacienteService.desasignarEspecialista(id, especialistaId);
          }
        }
        
        // 4. Crear nuevas asignaciones
        let asignacionesCreadas = 0;
        
        for (let i = 0; i < asignaciones.length; i++) {
          const asignacion = asignaciones[i];
          
          // Comprobar si esta asignación ya existe en el servidor
          const especialistaIdNuevo = String(asignacion.especialista_id);
          const existeEnServidor = asignacionesActuales.some(actual => {
            const especialistaIdActual = String(actual.especialista?.id || actual.especialista_id);
            return actual.activo && especialistaIdActual === especialistaIdNuevo;
          });

          // Si no existe, crear nueva asignación
          if (!existeEnServidor) {
            try {

              await pacienteService.asignarEspecialista(
                id,
                asignacion.especialista_id,
                asignacion.especialidad_id,
                asignacion.motivo_asignacion || "Asignación desde formulario de paciente"
              );
              
              asignacionesCreadas++;

            } catch (errorAsignacion) {
              console.error(`❌ Error creando asignación para especialista ${asignacion.especialista_id}:`, errorAsignacion);
              showNotification(`Error al asignar especialista ${asignacion.especialista_nombre}`, 'warning');
              // Continuar con las demás asignaciones
            }
          } else {
           }
        }

      } catch (error) {
        console.error('❌ Error gestionando asignaciones:', error);
        showNotification('Error al actualizar asignaciones de especialistas', 'error');
      }
      
      showNotification('Paciente actualizado con éxito', 'success');
      
    } else {
      
      // Crear nuevo paciente
      const response = await pacienteService.createPaciente({
        ...dataToSend,
        asignaciones: asignaciones.map(asignacion => ({
          especialidad_id: parseInt(asignacion.especialidad_id),
          especialista_id: parseInt(asignacion.especialista_id),
          motivo_asignacion: asignacion.motivo_asignacion || 'Asignación desde formulario de paciente'
        }))
      });
      const nuevoPacienteId = response.data.id || response.data.paciente?.id;
      pacienteId = nuevoPacienteId;
      
      
      if (!nuevoPacienteId) {
        throw new Error('No se pudo obtener el ID del nuevo paciente');
      }
      
      // Guardar apoderados para el nuevo paciente
      if (apoderados && apoderados.length > 0) {
        for (const apoderado of apoderados) {
          await pacienteService.createApoderado(nuevoPacienteId, apoderado);
        }
      }

      
      
      showNotification('Paciente creado con éxito', 'success');
    }
    
    navigate('/pacientes');
  } catch (error) {
    console.error('❌ Error saving patient:', error);
    let errorMsg = `Error al ${isEdit ? 'actualizar' : 'crear'} el paciente`;
    
    if (error.response && error.response.data && error.response.data.message) {
      errorMsg = error.response.data.message;
    } else if (error.message) {
      errorMsg += `: ${error.message}`;
    }
    
    showNotification(errorMsg, 'error');
  }
},
});

  useEffect(() => {
    const preventSubmit = (e) => {
      // Prevenir envío con Enter excepto en textareas
      if (e.key === 'Enter' && 
          e.target.tagName !== 'TEXTAREA' && 
          !e.target.closest('button[type="submit"]')) {
        e.preventDefault();
      }
    };
    
    // Aplicar el evento a todo el documento
    document.addEventListener('keydown', preventSubmit);
    
    // Limpiar al desmontar
    return () => {
      document.removeEventListener('keydown', preventSubmit);
    };
  }, []);

  const handleDniConsulta = async () => {
    const dni = formik.values.dni;
    
    if (!dni || dni.length !== 8) {
      showNotification('El DNI debe tener 8 dígitos', 'error');
      return;
    }
    
    try {
      setLoading(true);
      const data = await consultaService.consultarDNI(dni);
      
      formik.setFieldValue('nombres', data.nombres || '');
      formik.setFieldValue('apellidos', `${data.apellidoPaterno || ''} ${data.apellidoMaterno || ''}`.trim());
      
      showNotification('Datos de DNI recuperados correctamente', 'success');
    } catch (error) {
      console.error('Error al consultar DNI:', error);
      if (error.response && error.response.status === 404) {
        showNotification('DNI no encontrado en la base de datos externa', 'warning');
      } else {
        showNotification('Error al consultar el DNI. Por favor, ingrese los datos manualmente', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApoderadoDniConsulta = async (index) => {
    const dni = apoderados[index].dni;
    
    if (!dni || dni.length !== 8) {
      showNotification('El DNI debe tener 8 dígitos', 'error');
      return;
    }
    
    try {
      setLoading(true);
      const data = await consultaService.consultarDNI(dni);
      
      const nuevosApoderados = [...apoderados];
      nuevosApoderados[index] = {
        ...nuevosApoderados[index],
        nombres: data.nombres || '',
        apellidos: `${data.apellidoPaterno || ''} ${data.apellidoMaterno || ''}`.trim(),
        isModified: !nuevosApoderados[index].isNew
      };
      
      setApoderados(nuevosApoderados);
      
      showNotification('Datos de DNI del apoderado recuperados correctamente', 'success');
    } catch (error) {
      console.error('Error al consultar DNI del apoderado:', error);
      if (error.response && error.response.status === 404) {
        showNotification('DNI no encontrado en la base de datos externa', 'warning');
      } else {
        showNotification('Error al consultar el DNI. Por favor, ingrese los datos manualmente', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  // Manejar cambio de imagen
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      formik.setFieldValue('fotoFile', file);
      
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Funciones para manejar apoderados
  const agregarApoderado = () => {
    if (apoderados.length >= 2) {
      showNotification('Máximo 2 apoderados permitidos', 'warning');
      return;
    }
    
    setApoderados([...apoderados, {
      id: Date.now(), // Esto es un ID temporal que se elimina antes de enviar al servidor
      isNew: true,
      nombres: 'Nuevo Apoderado',
      apellidos: 'Sin Apellidos',
      dni: '',
      parentesco: '',
      telefono: '',
      email: '',
      direccion: '',
      es_principal: false,
      activo: true
    }]);
  };

  const actualizarApoderado = (index, field, value) => {
    const nuevosApoderados = [...apoderados];
    nuevosApoderados[index] = {
      ...nuevosApoderados[index],
      [field]: value,
      isModified: !nuevosApoderados[index].isNew
    };
    setApoderados(nuevosApoderados);
  };

  const eliminarApoderado = async (index) => {
    const apoderado = apoderados[index];
    
    if (!apoderado.isNew && isEdit) {
      try {
        await pacienteService.deleteApoderado(id, apoderado.id);
        showNotification('Apoderado eliminado correctamente', 'success');
      } catch (error) {
        showNotification('Error al eliminar apoderado', 'error');
        return;
      }
    }
    
    const nuevosApoderados = apoderados.filter((_, i) => i !== index);
    setApoderados(nuevosApoderados);
  };

  // Función de navegación en el stepper
  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const steps = [
    {
      label: 'Información Personal',
      icon: <AccountCircleIcon />,
      description: 'Datos básicos del paciente',
      content: (
        <Card elevation={3} sx={{ 
          borderRadius: 2,
          mt: 2,
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
          }
        }}>
          <Box sx={{ 
            p: 2, 
            backgroundColor: '#6a1b9a', // Morado oscuro
            color: 'white',
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <AccountCircleIcon />
            <Typography variant="h6" fontWeight="bold">
              Información Personal
            </Typography>
          </Box>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} sx={{ mb: 2 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f3e5f5', // Morado muy claro
                    border: '1px solid',
                    borderColor: '#9c27b0', // Morado medio
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <InfoIcon sx={{ color: '#9c27b0' }} />
                  <Typography variant="body2" sx={{ color: '#4a148c' }}> 
                    Para consultar los datos automáticamente por DNI, ingrese el número y presione Enter. Si el DNI no se encuentra, deberá completar los datos manualmente.
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <Avatar
                  src={previewImage || '/assets/images/default-user.png'}
                  alt={`${formik.values.nombres} ${formik.values.apellidos}`}
                  sx={{ width: 120, height: 120, mb: 2, boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }}
                />
                <Box sx={{ width: '100%', mt: 1 }}>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="foto-upload"
                    type="file"
                    onChange={handleImageChange}
                  />
                  <label htmlFor="foto-upload">
                    <Button 
                      component="span" 
                      variant="outlined" 
                      startIcon={<CloudUploadIcon />}
                      fullWidth
                      sx={{ borderRadius: 8 }}
                    >
                      Subir Foto
                    </Button>
                  </label>
                </Box>
              </Grid>
              <Grid item xs={12} md={8}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      id="nombres"
                      name="nombres"
                      label="Nombres *"
                      value={formik.values.nombres}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.nombres && Boolean(formik.errors.nombres)}
                      helperText={formik.touched.nombres && formik.errors.nombres}
                      InputProps={{
                        startAdornment: <PersonIcon color="primary" sx={{ mr: 1 }} />
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      id="apellidos"
                      name="apellidos"
                      label="Apellidos *"
                      value={formik.values.apellidos}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.apellidos && Boolean(formik.errors.apellidos)}
                      helperText={formik.touched.apellidos && formik.errors.apellidos}
                      InputProps={{
                        startAdornment: <PersonIcon color="primary" sx={{ mr: 1 }} />
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      id="dni"
                      name="dni"
                      label="DNI"
                      value={formik.values.dni}
                      onChange={(e) => {
                        formik.handleChange(e);
                        // Validación en tiempo real para asegurar que solo contiene números
                        if (e.target.value && !/^\d*$/.test(e.target.value)) {
                          showNotification('El DNI debe contener solo números', 'warning');
                        }
                      }}
                      onBlur={formik.handleBlur}
                     onKeyDown={(e) => {
                        // Prevenir el envío del formulario en Enter
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleDniConsulta(e);
                        }
                      }}
                      helperText={
                        loading && formik.values.dni 
                          ? "Consultando DNI..." 
                          : "Presiona Enter para consultar DNI automáticamente"
                      }
                      InputProps={{
                        startAdornment: <BadgeIcon color="primary" sx={{ mr: 1 }} />,
                        endAdornment: loading && formik.values.dni ? (
                          <CircularProgress size={20} />
                        ) : formik.values.dni && formik.values.dni.length === 8 ? (
                          <form onSubmit={(e) => e.preventDefault()}>
                            <Tooltip title="Presiona para consultar">
                              <IconButton 
                                size="small" 
                                type="button"
                                onClick={() => handleDniConsulta({ key: 'Enter' })}
                              >
                                <SearchIcon color="primary" fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </form>
                        ) : null,
                      }}
                      inputProps={{ maxLength: 8 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DatePicker
                      label="Fecha de Nacimiento"
                      value={formik.values.fecha_nacimiento}
                      onChange={(date) => formik.setFieldValue('fecha_nacimiento', date)}
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          fullWidth 
                          InputProps={{
                            startAdornment: <CakeIcon color="primary" sx={{ mr: 1 }} />,
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel id="sexo-label">Sexo</InputLabel>
                      <Select
                        labelId="sexo-label"
                        id="sexo"
                        name="sexo"
                        value={formik.values.sexo || ''}
                        label="Sexo"
                        onChange={(e) => {
                          // Establece null si se selecciona una cadena vacía
                          const value = e.target.value === '' ? null : e.target.value;
                          formik.setFieldValue('sexo', value);
                        }}
                        onBlur={formik.handleBlur}
                        startAdornment={<WcIcon color="primary" sx={{ mr: 1 }} />}
                      >
                        <MenuItem value="">Seleccione...</MenuItem>
                        <MenuItem value="M">Masculino</MenuItem>
                        <MenuItem value="F">Femenino</MenuItem>
                        <MenuItem value="Otro">Otro</MenuItem>
                      </Select>
                      <FormHelperText>
                        Seleccione una opción específica
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel id="estado-label">Estado</InputLabel>
                      <Select
                        labelId="estado-label"
                        id="estado"
                        name="estado"
                        value={formik.values.estado}
                        label="Estado"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        startAdornment={<CheckCircleIcon color="primary" sx={{ mr: 1 }} />}
                      >
                        <MenuItem value="Activo">Activo</MenuItem>
                        <MenuItem value="Inactivo">Inactivo</MenuItem>
                        <MenuItem value="De alta">De alta</MenuItem>
                        <MenuItem value="Pausa">En pausa</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ),
    },
    {
      label: 'Información de Contacto',
      icon: <ContactPhoneIcon />,
      description: 'Datos de contacto del paciente',
      content: (
        <Card elevation={3} sx={{ 
          borderRadius: 2,
          mt: 2,
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
          }
        }}>
          <Box sx={{ 
            p: 2, 
            backgroundColor: '#9c27b0', // Morado medio
            color: 'white',
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <ContactPhoneIcon />
            <Typography variant="h6" fontWeight="bold">
              Información de Contacto
            </Typography>
          </Box>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="direccion"
                  name="direccion"
                  label="Dirección"
                  value={formik.values.direccion}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  multiline
                  rows={2}
                  InputProps={{
                    startAdornment: <HomeIcon color="secondary" sx={{ mr: 1, mt: 1 }} />
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  id="telefono"
                  name="telefono"
                  label="Teléfono"
                  value={formik.values.telefono}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  InputProps={{
                    startAdornment: <PhoneIcon color="secondary" sx={{ mr: 1 }} />
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  id="whatsapp"
                  name="whatsapp"
                  label="WhatsApp"
                  value={formik.values.whatsapp}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  InputProps={{
                    startAdornment: <PhoneIcon color="secondary" sx={{ mr: 1 }} />
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="email"
                  name="email"
                  label="Correo Electrónico"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  InputProps={{
                    startAdornment: <EmailIcon color="secondary" sx={{ mr: 1 }} />
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  id="telefono_emergencia"
                  name="telefono_emergencia"
                  label="Teléfono de Emergencia"
                  value={formik.values.telefono_emergencia}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  InputProps={{
                    startAdornment: <ContactEmergencyIcon color="secondary" sx={{ mr: 1 }} />
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  id="contacto_emergencia"
                  name="contacto_emergencia"
                  label="Contacto de Emergencia"
                  value={formik.values.contacto_emergencia}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  InputProps={{
                    startAdornment: <ContactEmergencyIcon color="secondary" sx={{ mr: 1 }} />
                  }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ),
    },
    {
      label: 'Información Académica y Laboral',
      icon: <SchoolIcon />,
      description: 'Datos académicos y laborales del paciente',
      content: (
        <Card elevation={3} sx={{ 
          borderRadius: 2,
          mt: 2,
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
          }
        }}>
          <Box sx={{ 
            p: 2, 
            backgroundColor: '#ba68c8', // Morado claro
            color: 'white',
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <SchoolIcon />
            <Typography variant="h6" fontWeight="bold">
              Información Académica y Laboral
            </Typography>
          </Box>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  id="grado_academico"
                  name="grado_academico"
                  label="Grado Académico"
                  value={formik.values.grado_academico}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  InputProps={{
                    startAdornment: <SchoolIcon color="info" sx={{ mr: 1 }} />
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  id="escolaridad"
                  name="escolaridad"
                  label="Escolaridad"
                  value={formik.values.escolaridad}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  InputProps={{
                    startAdornment: <SchoolIcon color="info" sx={{ mr: 1 }} />
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="institucion_educativa"
                  name="institucion_educativa"
                  label="Institución Educativa"
                  value={formik.values.institucion_educativa}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  InputProps={{
                    startAdornment: <SchoolIcon color="info" sx={{ mr: 1 }} />
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="trabajo_actual"
                  name="trabajo_actual"
                  label="Trabajo Actual"
                  value={formik.values.trabajo_actual}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  InputProps={{
                    startAdornment: <WorkIcon color="info" sx={{ mr: 1 }} />
                  }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ),
    },
    {
      label: 'Información Médica',
      icon: <LocalHospitalIcon />,
      description: 'Datos médicos del paciente',
      content: (
        <Card elevation={3} sx={{ 
          borderRadius: 2,
          mt: 2,
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
          }
        }}>
          <Box sx={{ 
            p: 2, 
            backgroundColor: '#8e24aa', // Otro tono de morado
            color: 'white',
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <LocalHospitalIcon />
            <Typography variant="h6" fontWeight="bold">
              Información Médica
            </Typography>
          </Box>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  id="grupo_sanguineo"
                  name="grupo_sanguineo"
                  label="Grupo Sanguíneo"
                  value={formik.values.grupo_sanguineo}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  InputProps={{
                    startAdornment: <LocalHospitalIcon color="error" sx={{ mr: 1 }} />
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel id="lateralidad-label">Lateralidad</InputLabel>
                  <Select
                    labelId="lateralidad-label"
                    id="lateralidad"
                    name="lateralidad"
                    value={formik.values.lateralidad || ''}
                    label="Lateralidad"
                    onChange={(e) => {
                      // Establece null si se selecciona una cadena vacía
                      const value = e.target.value === '' ? null : e.target.value;
                      formik.setFieldValue('lateralidad', value);
                    }}
                    onBlur={formik.handleBlur}
                    startAdornment={<PersonIcon color="error" sx={{ mr: 1 }} />}
                  >
                    <MenuItem value="">Seleccione...</MenuItem>
                    <MenuItem value="Diestro">Diestro</MenuItem>
                    <MenuItem value="Zurdo">Zurdo</MenuItem>
                    <MenuItem value="Ambidiestro">Ambidiestro</MenuItem>
                  </Select>
                  <FormHelperText>
                    Seleccione una opción específica
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  id="alergias"
                  name="alergias"
                  label="Alergias"
                  value={formik.values.alergias}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  multiline
                  rows={2}
                  InputProps={{
                    startAdornment: <LocalHospitalIcon color="error" sx={{ mr: 1, mt: 1 }} />
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  id="condiciones_preexistentes"
                  name="condiciones_preexistentes"
                  label="Condiciones Preexistentes"
                  value={formik.values.condiciones_preexistentes}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  multiline
                  rows={2}
                  InputProps={{
                    startAdornment: <LocalHospitalIcon color="error" sx={{ mr: 1, mt: 1 }} />
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  id="medicacion_actual"
                  name="medicacion_actual"
                  label="Medicación Actual"
                  value={formik.values.medicacion_actual}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  multiline
                  rows={2}
                  InputProps={{
                    startAdornment: <LocalHospitalIcon color="error" sx={{ mr: 1, mt: 1 }} />
                  }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ),
    },
    // PASO: Asignación de Especialidades
    {
      label: 'Asignación de Especialistas',
      icon: <MedicalServicesIcon />,
      description: 'Asignar especialistas al paciente',
      content: (
        <Card elevation={3} sx={{ borderRadius: 2, mt: 2 }}>
          <Box sx={{ 
            p: 2, 
            backgroundColor: '#673ab7',
            color: 'white',
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
          }}>
            <Typography variant="h6" fontWeight="bold">
              <MedicalServicesIcon sx={{ mr: 1 }} />
              Asignación de Especialistas
            </Typography>
          </Box>
          <CardContent sx={{ p: 3 }}>

            <Alert severity="info" sx={{ mb: 3 }}>
              <AlertTitle>ℹ️ Información sobre Historias Clínicas</AlertTitle>
              <Typography variant="body2">
                • <strong>Misma especialidad:</strong> Los especialistas compartirán una Historia Clínica por especialidad<br/>
                • <strong>Diferentes especialidades:</strong> Se crearán Historias Clínicas separadas por cada especialidad
              </Typography>
            </Alert>

            <Grid container spacing={2}>
              <Grid item xs={12} md={5}>
                <FormControl fullWidth>
                  <InputLabel>Especialidad</InputLabel>
                  <Select
                    value={nuevaAsignacion.especialidad_id || ''}
                    onChange={(e) => {
                      setNuevaAsignacion({
                        ...nuevaAsignacion,
                        especialidad_id: e.target.value,
                        especialista_id: '' // Resetear especialista
                      });
                    }}
                    label="Especialidad"
                  >
                    <MenuItem value="">Seleccione una especialidad...</MenuItem>
                    {especialidades.map(especialidad => {
                      // NUEVO: Mostrar cuántos especialistas ya están asignados de esta especialidad
                      const cantidadAsignados = asignaciones.filter(
                        a => String(a.especialidad_id) === String(especialidad.id)
                      ).length;
                      
                      return (
                        <MenuItem key={especialidad.id} value={String(especialidad.id)}>
                          {especialidad.nombre}
                          {cantidadAsignados > 0 && (
                            <Chip 
                              label={`${cantidadAsignados} asignado${cantidadAsignados > 1 ? 's' : ''}`}
                              size="small" 
                              color="primary" 
                              variant="outlined"
                              sx={{ ml: 1 }}
                            />
                          )}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={5}>
                <FormControl fullWidth disabled={!nuevaAsignacion.especialidad_id}>
                  <InputLabel>
                    {!nuevaAsignacion.especialidad_id 
                      ? "Primero seleccione una especialidad" 
                      : "Especialista"}
                  </InputLabel>
                  <Select
                    value={nuevaAsignacion.especialista_id || ''}
                    onChange={(e) => setNuevaAsignacion({
                      ...nuevaAsignacion,
                      especialista_id: e.target.value
                    })}
                    label="Especialista"
                  >
                    <MenuItem value="">Seleccione un especialista...</MenuItem>
                    {nuevaAsignacion.especialidad_id && 
                      getEspecialistasPorEspecialidad(nuevaAsignacion.especialidad_id).map(especialista => {
                        // NUEVO: Verificar si este especialista ya está asignado
                        const yaAsignado = asignaciones.some(
                          a => String(a.especialista_id) === String(especialista.id)
                        );
                        
                        return (
                          <MenuItem 
                            key={especialista.id} 
                            value={String(especialista.id)}
                            disabled={yaAsignado}
                          >
                            {especialista.nombreCompleto || 'Sin nombre'}
                            {yaAsignado && (
                              <Chip 
                                label="Ya asignado" 
                                size="small" 
                                color="warning" 
                                variant="outlined"
                                sx={{ ml: 1 }}
                              />
                            )}
                          </MenuItem>
                        );
                      })
                    }
                  </Select>
                  {nuevaAsignacion.especialidad_id && 
                   getEspecialistasPorEspecialidad(nuevaAsignacion.especialidad_id).length === 0 && (
                    <FormHelperText error>
                      No hay especialistas disponibles para esta especialidad
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={2}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  sx={{ height: '56px' }}
                  disabled={!nuevaAsignacion.especialidad_id || !nuevaAsignacion.especialista_id}
                  onClick={agregarAsignacion}
                >
                  <AddIcon sx={{ mr: 1 }} />
                  Agregar
                </Button>
              </Grid>
            </Grid>
            
            {/* Tabla de asignaciones */}
            {asignaciones.length > 0 ? (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                  Especialistas asignados ({asignaciones.length}):
                </Typography>

              

                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableRow>
                        <TableCell width="40%">Especialidad</TableCell>
                        <TableCell width="40%">Especialista</TableCell>
                        <TableCell width="20%" align="center">Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {asignaciones.map((asignacion, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Chip 
                              label={asignacion.especialidad_nombre || getNombreEspecialidad(asignacion.especialidad_id)}
                              color="primary"
                              variant="outlined"
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={asignacion.especialista_nombre || getNombreEspecialista(asignacion.especialista_id)}
                              color="secondary"
                              variant="outlined"
                              size="small"
                              icon={<PersonIcon />}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => eliminarAsignacion(index)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ) : (
              <Alert severity="info" sx={{ mt: 2 }}>
                No hay especialistas asignados. Agregue al menos un especialista.
              </Alert>
            )}
          </CardContent>
        </Card>
      ),
    },
    {
      label: 'Apoderados',
      icon: <PersonAddIcon />,
      description: 'Información de apoderados del paciente',
      content: (
        <Card elevation={3} sx={{ 
          borderRadius: 2,
          mt: 2,
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
          }
        }}>
          <Box sx={{ 
            p: 2, 
            backgroundColor: '#7b1fa2', // Morado profundo
            color: 'white',
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonAddIcon />
              <Typography variant="h6" fontWeight="bold">
                Apoderados ({apoderados.length}/2)
              </Typography>
            </Box>
            <Button
              variant="contained"
              sx={{
                bgcolor: '#e1bee7', // Lavanda claro
                color: '#4a148c', // Morado muy oscuro
                '&:hover': {
                  bgcolor: '#ce93d8', // Lavanda más oscuro al hacer hover
                },
                borderRadius: 8
              }}
              startIcon={<PersonAddIcon />}
              onClick={agregarApoderado}
              disabled={apoderados.length >= 2}
            >
              Agregar Apoderado
            </Button>
          </Box>
          <CardContent sx={{ p: 3 }}>
            {apoderados.length === 0 ? (
              <Box textAlign="center" py={5}>
                <PersonAddIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography color="textSecondary" gutterBottom>
                  No hay apoderados registrados
                </Typography>
                <Button 
                  variant="contained" 
                  startIcon={<PersonAddIcon />}
                  onClick={agregarApoderado}
                  sx={{ mt: 2, borderRadius: 8 }}
                >
                  Agregar Apoderado
                </Button>
              </Box>
            ) : (
              <Grid container spacing={3}>
                {apoderados.map((apoderado, index) => (
                  <Grid item xs={12} key={apoderado.id}>
                    <Paper 
                      elevation={2} 
                      sx={{ 
                        p: 0, 
                        borderRadius: 2,
                        overflow: 'hidden',
                        borderLeft: '4px solid',
                        borderColor: '#7b1fa2', // Morado profundo
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          boxShadow: '0 5px 15px rgba(0,0,0,0.1)'
                        }
                      }}
                    >
                      <Box sx={{ 
                        p: 2, 
                        backgroundColor: 'background.paper', 
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        borderBottom: '1px solid rgba(0,0,0,0.08)'
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar sx={{ 
                            bgcolor: '#ba68c8', // Morado claro
                            color: 'white',
                            mr: 2
                          }}>
                            {index + 1}
                          </Avatar>
                          <Typography variant="subtitle1" fontWeight="bold">
                            Apoderado {index + 1}
                          </Typography>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={apoderado.es_principal || false}
                                onChange={(e) => actualizarApoderado(index, 'es_principal', e.target.checked)}
                                
                                color="primary"
                              />
                            }
                            label="Es principal"
                            sx={{ ml: 2 }}
                          />
                        </Box>
                        <IconButton 
                          color="error" 
                          onClick={() => eliminarApoderado(index)}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                      
                      <Box sx={{ p: 3 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              label="Nombres"
                              value={apoderado.nombres}
                              onChange={(e) => actualizarApoderado(index, 'nombres', e.target.value)}
                              InputProps={{
                                startAdornment: <PersonIcon color="warning" sx={{ mr: 1 }} />
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              label="Apellidos"
                              value={apoderado.apellidos}
                              onChange={(e) => actualizarApoderado(index, 'apellidos', e.target.value)}
                              InputProps={{
                                startAdornment: <PersonIcon color="warning" sx={{ mr: 1 }} />
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              label="DNI"
                              value={apoderado.dni}
                              onChange={(e) => actualizarApoderado(index, 'dni', e.target.value)}
                              onKeyDown={(e) => {
                                  // Prevenir el envío del formulario en Enter
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleApoderadoDniConsulta(index, e);
                                  }
                                }}
                              InputProps={{
                                startAdornment: <BadgeIcon color="warning" sx={{ mr: 1 }} />,
                                endAdornment: apoderado.dni && apoderado.dni.length === 8 ? (
                                <Tooltip title="Presiona para consultar">
                                  <IconButton 
                                    size="small" 
                                    onClick={() => handleDniConsulta({ key: 'Enter' })}
                                  >
                                    <SearchIcon color="primary" fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                ) : null,
                              }}
                              inputProps={{ maxLength: 8 }}
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              label="Parentesco"
                              value={apoderado.parentesco}
                              onChange={(e) => actualizarApoderado(index, 'parentesco', e.target.value)}
                              InputProps={{
                                startAdornment: <ContactMailIcon color="warning" sx={{ mr: 1 }} />
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              label="Teléfono"
                              value={apoderado.telefono}
                              onChange={(e) => actualizarApoderado(index, 'telefono', e.target.value)}
                              InputProps={{
                                startAdornment: <PhoneIcon color="warning" sx={{ mr: 1 }} />
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              label="Email"
                              value={apoderado.email}
                              onChange={(e) => actualizarApoderado(index, 'email', e.target.value)}
                              InputProps={{
                                startAdornment: <EmailIcon color="warning" sx={{ mr: 1 }} />
                              }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Dirección"
                              value={apoderado.direccion}
                              onChange={(e) => actualizarApoderado(index, 'direccion', e.target.value)}
                              multiline
                              rows={2}
                              InputProps={{
                                startAdornment: <HomeIcon color="warning" sx={{ mr: 1, mt: 1 }} />
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}
          </CardContent>
        </Card>
      ),
    },
    {
      label: 'Notas Administrativas',
      icon: <NotesIcon />,
      description: 'Notas administrativas del paciente',
      content: (
        <Card elevation={3} sx={{ 
          borderRadius: 2,
          mt: 2,
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
          }
        }}>
          <Box sx={{ 
            p: 2, 
            backgroundColor: '#ab47bc', // Otro tono morado
            color: 'white',
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <NotesIcon />
            <Typography variant="h6" fontWeight="bold">
              Notas Administrativas
            </Typography>
          </Box>
          <CardContent sx={{ p: 3 }}>
            <TextField
              fullWidth
              id="notas_administrativas"
              name="notas_administrativas"
              label="Notas Administrativas"
              value={formik.values.notas_administrativas}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              multiline
              rows={4}
              placeholder="Ingrese aquí cualquier nota o comentario administrativo relevante para este paciente"
              InputProps={{
                startAdornment: <NotesIcon color="success" sx={{ mr: 1, mt: 1 }} />
              }}
            />
          </CardContent>
        </Card>
      ),
    },
  ];

  return (
    <Box sx={{ flexGrow: 1 }}>
      <PageHeader 
        title={isEdit ? 'Editar Paciente' : 'Nuevo Paciente'}
        subtitle={isEdit ? `Modificando datos de ${formik.values.nombres} ${formik.values.apellidos}` : 'Ingrese los datos del nuevo paciente'}
        icon={
          <Avatar
            src={previewImage || '/assets/images/default-user.png'}
            sx={{ 
              width: 56, 
              height: 56, 
              border: '3px solid #fff',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}
          />
        }
        breadcrumbs={[
          { text: 'Dashboard', to: '/dashboard' },
          { text: 'Pacientes', to: '/pacientes' },
          { text: isEdit ? 'Editar Paciente' : 'Nuevo Paciente' }
        ]}
      />
      
      {loading ? (
        <Box display="flex" justifyContent="center" my={5}>
          <CircularProgress />
        </Box>
      ) : (
        <form onSubmit={formik.handleSubmit}>
          <Box sx={{ width: '100%' }}>
            <Stepper activeStep={activeStep} orientation="vertical">
              {steps.map((step, index) => (
                <Step key={step.label}>
                  <StepLabel 
                    optional={step.description ? <Typography variant="caption">{step.description}</Typography> : null}
                    StepIconProps={{
                      icon: step.icon
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight="medium">{step.label}</Typography>
                  </StepLabel>
                  <StepContent>
                    {step.content}
                    <Box sx={{ mb: 2, mt: 3 }}>
                      <div>
                        <Button
                          disabled={index === 0}
                          onClick={handleBack}
                          sx={{ mr: 1 }}
                          startIcon={<ChevronLeftIcon />}
                        >
                          Atrás
                        </Button>
                        {index === steps.length - 1 ? (
                          <Button 
                            variant="contained" 
                            color="primary" 
                            type="submit" 
                            disabled={formik.isSubmitting}
                            startIcon={formik.isSubmitting ? <CircularProgress size={20} /> : <SaveIcon />}
                            sx={{ borderRadius: 8 }}
                          >
                            {isEdit ? 'Actualizar Paciente' : 'Crear Paciente'}
                          </Button>
                        ) : (
                          <Button 
                            variant="contained" 
                            onClick={handleNext} 
                            sx={{ borderRadius: 8 }}
                          >
                            Siguiente
                          </Button>
                        )}
                      </div>
                    </Box>
                  </StepContent>
                </Step>
              ))}
            </Stepper>
            
            {activeStep === steps.length && (
              <Paper square elevation={0} sx={{ p: 3 }}>
                <Typography>¡Formulario completado!</Typography>
                <Button onClick={handleBack} sx={{ mt: 1, mr: 1 }}>
                  Atrás
                </Button>
                <Button 
                  variant="contained" 
                  color="primary" 
                  type="submit" 
                  sx={{ mt: 1, mr: 1 }}
                >
                  {isEdit ? 'Actualizar Paciente' : 'Crear Paciente'}
                </Button>
              </Paper>
            )}
          </Box>
          
          {/* Botones de acción fijos en la parte inferior */}
          <Box 
            display="flex" 
            justifyContent="flex-end" 
            mt={4} 
            gap={2}
            position="sticky" 
            bottom={16} 
            pb={2}
            zIndex={2}
          >
            {formik.errors && Object.keys(formik.errors).length > 0 && formik.submitCount > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 2,
                  borderRadius: 8,
                  backgroundColor: 'error.light',
                  color: 'error.contrastText',
                  mr: 'auto'
                }}
              >
                <ErrorIcon sx={{ mr: 1 }} />
                <Typography variant="body2" fontWeight="medium">
                  Por favor corrija los errores antes de continuar
                </Typography>
              </Box>
            )}
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => navigate('/pacientes')}
              startIcon={<ChevronLeftIcon />}
              sx={{ borderRadius: 8 }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={formik.isSubmitting}
              startIcon={formik.isSubmitting ? <CircularProgress size={20} /> : <SaveIcon />}
              sx={{ 
                borderRadius: 8,
                px: 4,
                boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                bgcolor: '#6a1b9a', // Morado oscuro
                '&:hover': {
                  bgcolor: '#4a148c', // Morado más oscuro
                } 
              }}
            >
              {isEdit ? 'Actualizar Paciente' : 'Crear Paciente'}
            </Button>
          </Box>
        </form>
      )}
    </Box>
  );
};

export default PacienteForm;